import * as THREE from 'three';

// Shared "base interior" view (spec section 8, extended) -- the OLOS / LOS model.
//
//   OLOS (Outside Line Of Sight): when the local player is in THIRD PERSON and under their own roof,
//   the outside world fades to near-black so a zoomed-out camera cannot scout threats (door campers,
//   raid towers) over the walls.
//   LOS (Line Of Sight): the real world, rendered normally, in the directions the player's HEAD can
//   actually see out -- a generous cone through every nearby opening (open door / window). Anything
//   the head cannot see through an opening stays OLOS, EVEN where the third-person camera can see it
//   over a wall (that explicit darken is the whole point -- walls alone don't occlude an overhead
//   camera).
//
// First person has no occlusion at all (handled in ./occlusion): you only see where you look, so the
// full world renders with no tricks.
//
// The dark + the LOS cones are computed per fragment in world space against the player head, so the
// ground, trees and rocks share one identical reveal that tracks the head every frame (parallax: the
// cones sweep as you move) and feathers at the cone edges + the home radius -- it fades, never cuts.

const MAX_OPENINGS = 6; // nearest openings considered per frame (also the GLSL array size)

export const INTERIOR = {
  uInterior: { value: 0 },                   // 0 outside .. 1 fully inside (lerped)
  uPlayerXZ: { value: new THREE.Vector2() }, // head world XZ = LOS cone origin + home-radius centre
  uOpenCount: { value: 0 },                  // active opening count this frame
  uOpenDir: { value: Array.from({ length: MAX_OPENINGS }, () => new THREE.Vector2(0, -1)) }, // head->opening dirs
  uOpenCos: { value: new Array<number>(MAX_OPENINGS).fill(2) }, // cos(half-angle); >1 => inactive (dot can't exceed)
  uOpenGain: { value: new Array<number>(MAX_OPENINGS).fill(0) }, // GAZE gate 0..1: how much this cone reveals (eased by facing)
  // Reveal RANGE: in-cone world renders normally out to Near, then fades back into the dark by Far --
  // you see the meadow through the window, not the whole map (owner: generous band, F2-tunable).
  uRevealNear: { value: 80 },
  uRevealFar: { value: 150 },
};

// LOS cone half-angle widens as the head nears the opening: standing in a doorway you see a broad
// arc out of it; deep in the room a far door is only a narrow slit.
const WIDE = 1.28;   // ~73 deg half-angle (~146 deg arc) when standing in the opening
const NARROW = 0.5;  // ~29 deg half-angle for a distant opening
const NEAR_D = 2.0;  // at/under this distance the cone is fully WIDE
const FAR_D = 11.0;  // at/over this distance the cone is fully NARROW

function smooth01(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

export interface Opening { x: number; z: number; gain?: number; }

// Update the shared state each frame. openings are world-XZ centres of the openings the head can see
// through; each becomes a LOS cone (dir = head->opening, half-angle by proximity) scaled by its GAZE
// gain (0..1 -- how much the player is actually facing that opening; caller eases it over time so
// reveals FADE IN as you turn, mimicking a first-person frustum). Extra slots are marked inactive.
export function setInterior(factor: number, px: number, pz: number, openings: ReadonlyArray<Opening>): void {
  INTERIOR.uInterior.value = factor;
  INTERIOR.uPlayerXZ.value.set(px, pz);
  const n = Math.min(openings.length, MAX_OPENINGS);
  INTERIOR.uOpenCount.value = n;
  for (let i = 0; i < n; i++) {
    let dx = openings[i].x - px;
    let dz = openings[i].z - pz;
    const dist = Math.hypot(dx, dz) || 1e-3;
    dx /= dist; dz /= dist;
    const half = NARROW + (WIDE - NARROW) * (1 - smooth01(NEAR_D, FAR_D, dist));
    INTERIOR.uOpenDir.value[i].set(dx, dz);
    INTERIOR.uOpenGain.value[i] = openings[i].gain ?? 1;
    INTERIOR.uOpenCos.value[i] = Math.cos(half);
  }
  for (let i = n; i < MAX_OPENINGS; i++) INTERIOR.uOpenCos.value[i] = 2;
}

// Point a compiled shader's uniforms at the shared interior state (every world material shares the
// same objects, so one setInterior drives them all).
export function bindInteriorUniforms(shader: THREE.WebGLProgramParametersWithUniforms): void {
  shader.uniforms.uInterior = INTERIOR.uInterior;
  shader.uniforms.uPlayerXZ = INTERIOR.uPlayerXZ;
  shader.uniforms.uOpenCount = INTERIOR.uOpenCount;
  shader.uniforms.uOpenDir = INTERIOR.uOpenDir;
  shader.uniforms.uOpenCos = INTERIOR.uOpenCos;
  shader.uniforms.uOpenGain = INTERIOR.uOpenGain;
  shader.uniforms.uRevealNear = INTERIOR.uRevealNear;
  shader.uniforms.uRevealFar = INTERIOR.uRevealFar;
}

export const INTERIOR_FRAG_DECL =
  `uniform float uInterior; uniform vec2 uPlayerXZ; uniform int uOpenCount; uniform vec2 uOpenDir[${MAX_OPENINGS}]; uniform float uOpenCos[${MAX_OPENINGS}]; uniform float uOpenGain[${MAX_OPENINGS}]; uniform float uRevealNear; uniform float uRevealFar;`;

// The fragment-shader darken, parameterised on a world-XZ expression so terrain and props share the
// exact same home radius and LOS cones. LOS = max reveal over the openings (1 = real world, fully
// revealed); the cone edge feathers (0.14 in cos space) and the radius feathers, so the reveal fades.
export function interiorDarkGLSL(worldXZ: string): string {
  return `{
    float _pd = distance(${worldXZ}, uPlayerXZ);
    float _bd = smoothstep(11.0, 22.0, _pd);
    vec2 _tf = normalize(${worldXZ} - uPlayerXZ + vec2(1e-4));
    float _los = 0.0;
    for (int _i = 0; _i < ${MAX_OPENINGS}; _i++) {
      if (_i >= uOpenCount) break;
      // Cone reveal scaled by the eased GAZE gain: an opening behind the player's view reveals
      // nothing, and fades in as they turn toward it (third person mimicking a first-person frustum).
      _los = max(_los, uOpenGain[_i] * smoothstep(uOpenCos[_i] - 0.14, uOpenCos[_i], dot(_tf, uOpenDir[_i])));
    }
    // Reveal is RANGE-BOUND: full to uRevealNear, melting back into the dim by uRevealFar -- a
    // window shows the meadow outside, not a bright wedge running to the horizon.
    _los *= 1.0 - smoothstep(uRevealNear, uRevealFar, _pd);
    // Darken to a cool DIM, not black (owner ask): grass, trees and terrain stay readable as
    // silhouettes -- the world is still there -- while detail (players, loot, movement tells) is
    // crushed enough that the anti-scouting purpose holds.
    gl_FragColor.rgb = mix(gl_FragColor.rgb, gl_FragColor.rgb * vec3(0.13, 0.15, 0.20), uInterior * _bd * (1.0 - _los));
  }`;
}

// Inject the interior darken into a world-prop material (trees, rocks). Adds a world-XZ varying and
// applies the darken to the FINAL color (after fog), so props match the ground. Chains any existing
// onBeforeCompile / customProgramCacheKey (e.g. the leaf-wind sway) instead of clobbering it. Pass
// instanced=true for InstancedMesh materials so the world position folds in instanceMatrix.
type Compilable = THREE.Material & {
  onBeforeCompile: (shader: THREE.WebGLProgramParametersWithUniforms, renderer: THREE.WebGLRenderer) => void;
  customProgramCacheKey?: () => string;
};

export function applyInteriorDark(mat: Compilable, instanced = false): void {
  const prev = mat.onBeforeCompile;
  const prevKey = mat.customProgramCacheKey;
  mat.onBeforeCompile = (shader, renderer) => {
    if (prev) prev.call(mat, shader, renderer);
    bindInteriorUniforms(shader);
    const world = instanced
      ? '(modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xz'
      : '(modelMatrix * vec4(transformed, 1.0)).xz';
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vIWXZ;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n  vIWXZ = ${world};`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nvarying vec2 vIWXZ;\n${INTERIOR_FRAG_DECL}`)
      .replace('#include <fog_fragment>', `#include <fog_fragment>\n${interiorDarkGLSL('vIWXZ')}`);
  };
  mat.customProgramCacheKey = () => (prevKey ? prevKey.call(mat) : mat.type) + '|interior' + (instanced ? 'I' : '');
}
